import axios from 'axios';
import { toast } from 'react-toastify';
import api from '../config/axiosConfig';
import i18next from 'i18next';
import { STORAGE_KEYS } from '@/config/storageKeys';
import { TokenStore } from './TokenStore';
import { getCsrfToken } from '../utils/csrfCookie';

// Bare client for token refresh — it MUST bypass the intercepted `api`
// instance. If the refresh call 401s through the shared interceptor while
// isRefreshing=true, it gets parked in failedQueue and deadlocks the whole
// auth bootstrap (infinite loading spinner). It also must not carry the
// expired access token as an Authorization header.
const refreshClient = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  withCredentials: true, // send the httpOnly refreshToken cookie
  headers: {
    'Content-Type': 'application/json',
  },
  // A hung refresh call must fail, not hang: the interceptor awaits this
  // request while holding isRefreshing=true, so an unbounded stall would
  // park every subsequent 401 in failedQueue (infinite loading spinner).
  timeout: 10000,
});

export class AuthService {
  static getAccessToken(): string | null {
    return TokenStore.getActiveAccessToken();
  }

  static isImpersonating(): boolean {
    return TokenStore.isImpersonating();
  }

  static isAuthenticated(): boolean {
    return !!this.getAccessToken();
  }

  static clearTokens(): void {
    TokenStore.clear();
  }

  static async login(email: string, password: string): Promise<boolean> {
    try {
      const response = await api.post('/auth/login', { email, password });

      if (response.status === 200 && response.data.accessToken) {
        // A fresh login is never an impersonation — drop any brand pinned by
        // a previous session in this tab so a reload doesn't try to restore it.
        TokenStore.setImpersonationSession(null);
        TokenStore.setAccessToken(response.data.accessToken);

        toast.success(i18next.t('auth.loginSuccess'));
        return true;
      } else {
        toast.error(i18next.t('auth.loginFailed'));
        return false;
      }
    } catch (error: any) {
      if (error.response) {
        switch (error.response.status) {
          case 401:
            toast.error(i18next.t('auth.invalidCredentials'));
            break;
          case 403:
            toast.error(i18next.t('auth.accountNotVerified'));
            break;
          case 404:
            toast.error(i18next.t('auth.userNotFound'));
            break;
          default:
            toast.error(i18next.t('auth.loginFailed'));
        }
      } else {
        toast.error(i18next.t('auth.networkError'));
      }
      return false;
    }
  }

  static async logout(): Promise<void> {
    try {
      // The `api` instance's request interceptor attaches the current access
      // token automatically (see axiosConfig.ts), so this must run BEFORE
      // clearTokens() below removes it. The normal refresh token is sent
      // automatically via the httpOnly cookie (withCredentials); only the
      // impersonation session's refresh token — never cookied — needs to be
      // passed explicitly, mirroring refreshToken()'s pattern below.
      const impersonating = TokenStore.isImpersonating();
      const body = impersonating
        ? { refreshToken: TokenStore.getImpersonationRefreshToken() }
        : {};
      await api.post('/auth/logout', body);
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      TokenStore.clear();
      localStorage.removeItem(STORAGE_KEYS.CAMPAIGN_SOCIAL_POPUP_DISMISSED);
      window.location.href = '/signin';
    }
  }

  /**
   * Contract (the axios interceptor depends on it):
   * - resolves with a token  → refresh succeeded
   * - resolves with null     → session DEAD; tokens are already cleared, so
   *                            isAuthenticated() is false and a redirect to
   *                            /signin cannot be bounced back by route guards
   * - throws                 → TRANSIENT failure (network/timeout/5xx); the
   *                            tokens were never judged by the server and are
   *                            kept — the caller must NOT navigate.
   *
   * If impersonating, the impersonation session's own refresh token is sent
   * in the body — impersonation refresh tokens are never cookied (they must
   * not overwrite the admin's own refreshToken cookie). Otherwise the normal
   * refresh token cookie is sent automatically via withCredentials.
   */
  static async refreshToken(): Promise<string | null> {
    const impersonating = TokenStore.isImpersonating();
    const body = impersonating
      ? { refreshToken: TokenStore.getImpersonationRefreshToken() }
      : {};

    try {
      // refreshClient bypasses the shared `api` instance's interceptors (see
      // comment above), so the CSRF header it normally attaches has to be
      // set explicitly here too — /auth/refreshToken requires it whenever a
      // refreshToken cookie is present, which is true for any browser
      // session that has ever logged in (including admin sessions with an
      // impersonation session layered on top).
      const csrfToken = getCsrfToken();
      const response = await refreshClient.post('/auth/refreshToken', body, {
        headers: csrfToken ? { 'X-CSRF-Token': csrfToken } : undefined,
      });

      if (response.status === 200 && response.data.accessToken) {
        if (impersonating) {
          const session = TokenStore.getImpersonationSession();
          TokenStore.setImpersonationSession(
            session
              ? {
                  ...session,
                  accessToken: response.data.accessToken,
                  refreshToken:
                    response.data.refreshToken ?? session.refreshToken,
                }
              : null,
          );
        } else {
          TokenStore.setAccessToken(response.data.accessToken);
        }
        return response.data.accessToken;
      }

      // 2xx without an access token: broken server contract — session unusable.
      TokenStore.clear();
      return null;
    } catch (error: any) {
      console.error('Error refreshing token:', error);
      const status = error?.response?.status;
      if (status >= 400 && status < 500) {
        // The server judged the refresh token and rejected it
        // (401 invalid/revoked/missing, 403 forbidden, 404 user deleted, …).
        TokenStore.clear();
        return null;
      }
      // 5xx / timeout / no response: transient — keep the tokens.
      throw error;
    }
  }

  static async register(userData: any): Promise<boolean> {
    try {
      const response = await api.post('/auth/register', userData);

      if (response.status === 201 || response.status === 200) {
        toast.success(i18next.t('auth.registrationSuccess'));
        return true;
      } else {
        toast.error(i18next.t('auth.registrationFailed'));
        return false;
      }
    } catch (error: any) {
      if (error.response) {
        switch (error.response.status) {
          case 409:
            toast.error(i18next.t('auth.emailAlreadyExists'));
            break;
          default:
            toast.error(i18next.t('auth.registrationFailed'));
        }
      } else {
        toast.error(i18next.t('auth.networkError'));
      }
      return false;
    }
  }

  static async forgotPassword(email: string): Promise<boolean> {
    try {
      const response = await api.post('/auth/forgot-password', { email });

      if (response.status === 200) {
        toast.success(i18next.t('auth.passwordResetEmailSent'));
        return true;
      } else {
        toast.error(i18next.t('auth.passwordResetFailed'));
        return false;
      }
    } catch (error: any) {
      if (error.response && error.response.status === 404) {
        toast.error(i18next.t('auth.emailNotFound'));
      } else {
        toast.error(i18next.t('auth.passwordResetFailed'));
      }
      return false;
    }
  }

  static async resetPassword(
    token: string,
    newPassword: string,
  ): Promise<boolean> {
    try {
      const response = await api.post('/auth/reset-password', {
        token,
        newPassword,
      });

      if (response.status === 200) {
        toast.success(i18next.t('auth.passwordResetSuccess'));
        return true;
      } else {
        toast.error(i18next.t('auth.passwordResetFailed'));
        return false;
      }
    } catch (error: any) {
      if (error.response) {
        switch (error.response.status) {
          case 400:
            toast.error(i18next.t('auth.invalidToken'));
            break;
          case 404:
            toast.error(i18next.t('auth.userNotFound'));
            break;
          default:
            toast.error(i18next.t('auth.passwordResetFailed'));
        }
      } else {
        toast.error(i18next.t('auth.networkError'));
      }
      return false;
    }
  }
}
